import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Trash2, Plus, ExternalLink, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';

export function BlogManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    tag: 'Community',
    cta_text: '',
    cta_link: '/tanning'
  });
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  
  useEffect(() => {
    fetchPosts();
  }, []);
  
  const fetchPosts = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/blog/posts`);
      const data = await response.json();
      setPosts(data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreate = async () => {
    if (!formData.title || !formData.excerpt || !formData.content) {
      toast.error('Please fill in title, excerpt, and content');
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${backendUrl}/api/blog/posts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create post');
      }
      
      toast.success('Blog post created! Static HTML generated.');
      setShowCreateModal(false);
      setFormData({
        title: '',
        excerpt: '',
        content: '',
        tag: 'Community',
        cta_text: '',
        cta_link: '/tanning'
      });
      fetchPosts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };
  
  const handleDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This will remove the static HTML file.`)) {
      return;
    }
    
    setDeleting(slug);
    try {
      const response = await fetch(`${backendUrl}/api/blog/posts/${slug}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete post');
      }
      
      toast.success('Blog post deleted');
      fetchPosts();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDeleting(null);
    }
  };
  
  const handleRegenerate = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/blog/regenerate-index`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to regenerate index');
      }
      
      toast.success('Blog index regenerated');
    } catch (error) {
      toast.error(error.message);
    }
  };
  
  const tagColors = {
    'Featured Story': 'bg-amber-500',
    'Lifestyle': 'bg-blue-500',
    'Community': 'bg-green-500',
    'Wellness': 'bg-purple-500'
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">People of Eastend Blog</h2>
          <p className="text-muted-foreground">
            Manage static HTML blog posts for SEO/AEO (no JavaScript required)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleRegenerate}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Regenerate Index
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            New Story
          </Button>
        </div>
      </div>
      
      {/* Quick Links */}
      <Card className="p-4">
        <div className="flex items-center gap-4">
          <FileText className="w-5 h-5 text-muted-foreground" />
          <span className="text-sm font-medium">Quick Links:</span>
          <a 
            href="/blog/index.html" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            View Blog <ExternalLink className="w-3 h-3" />
          </a>
          <span className="text-muted-foreground">|</span>
          <span className="text-sm text-muted-foreground">
            {posts.length} stories published
          </span>
        </div>
      </Card>
      
      {/* Posts Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {posts.map((post) => (
          <Card key={post.slug} className="p-4" data-testid={`blog-post-${post.slug}`}>
            <div className="flex items-start justify-between mb-3">
              <Badge className={`${tagColors[post.tag] || 'bg-gray-500'} text-white text-xs`}>
                {post.tag}
              </Badge>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleDelete(post.slug)}
                disabled={deleting === post.slug}
                data-testid={`delete-${post.slug}`}
              >
                <Trash2 className="w-4 h-4 text-destructive" />
              </Button>
            </div>
            
            <h3 className="font-semibold mb-2 line-clamp-2">{post.title}</h3>
            <p className="text-sm text-muted-foreground mb-3 line-clamp-2">{post.excerpt}</p>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{post.date}</span>
              <a 
                href={post.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline flex items-center gap-1"
              >
                View <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </Card>
        ))}
      </div>
      
      {posts.length === 0 && (
        <Card className="p-12 text-center">
          <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No Blog Posts Yet</h3>
          <p className="text-muted-foreground mb-4">
            Create your first static HTML blog post for SEO/AEO.
          </p>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Story
          </Button>
        </Card>
      )}
      
      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Blog Story</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  placeholder="Why People Choose Tanning at Eastend"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  data-testid="blog-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="tag">Tag/Category</Label>
                <Select 
                  value={formData.tag} 
                  onValueChange={(v) => setFormData({ ...formData, tag: v })}
                >
                  <SelectTrigger data-testid="blog-tag-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Featured Story">Featured Story</SelectItem>
                    <SelectItem value="Lifestyle">Lifestyle</SelectItem>
                    <SelectItem value="Community">Community</SelectItem>
                    <SelectItem value="Wellness">Wellness</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="excerpt">Excerpt/Summary * (shown on blog index)</Label>
              <Textarea
                id="excerpt"
                placeholder="A short summary of the story that appears on the main blog page..."
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                rows={2}
                data-testid="blog-excerpt-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="content">Full Story Content * (separate paragraphs with blank lines)</Label>
              <Textarea
                id="content"
                placeholder="Write your story here...

Separate paragraphs with blank lines.

Each paragraph will be properly formatted in the final HTML."
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={8}
                data-testid="blog-content-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cta_text">Call-to-Action Text (optional)</Label>
                <Input
                  id="cta_text"
                  placeholder="Try it yourself!"
                  value={formData.cta_text}
                  onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="cta_link">CTA Link</Label>
                <Select 
                  value={formData.cta_link} 
                  onValueChange={(v) => setFormData({ ...formData, cta_link: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="/tanning">Tanning</SelectItem>
                    <SelectItem value="/laundry">Laundry</SelectItem>
                    <SelectItem value="/drinks">Fizze Drinks</SelectItem>
                    <SelectItem value="/eats">818 EATS</SelectItem>
                    <SelectItem value="/nails">Nails</SelectItem>
                    <SelectItem value="/">Home</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="bg-muted/50 p-3 rounded-lg text-sm">
              <strong>Note:</strong> This will generate a static HTML file at{' '}
              <code className="bg-muted px-1 rounded">/blog/stories/[slug].html</code>{' '}
              that can be crawled by search engines without JavaScript.
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating} data-testid="create-blog-btn">
              {creating ? 'Creating...' : 'Create Story'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
